import React, { useCallback, useEffect, useState } from 'react';
import { notification } from 'antd';

import Auth0RolesContext from '@totem/components/administration/auth0/roles/Auth0RolesContext';
import {
  IBAuth0Role,
  MultiStepResults,
  RoleUpdateRequest,
} from '@totem/components/administration/auth0/roles/types';
import { getToken } from '@totem/components/userContext/accountUtilities';
import { useAsyncJob } from '@totem/hooks/useAsyncJob';
import {
  AUTH0_ROLES_ENDPOINT,
  AUTH0_ROLES_REFRESH_ALL_ENDPOINT,
} from '@totem/utilities/endpoints';

type Props = {
  children: React.ReactNode;
};

const Auth0RolesContainer = ({ children }: Props) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IBAuth0Role[]>([]);
  const [editingRole, setEditingRole] = useState<IBAuth0Role | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [showSyncResults, setShowSyncResults] = useState(false);

  const {
    status: syncStatus,
    result: syncJobResult,
    error: syncJobError,
    start: startSync,
  } = useAsyncJob<MultiStepResults>(AUTH0_ROLES_REFRESH_ALL_ENDPOINT);

  const syncing = syncStatus === 'pending' || syncStatus === 'running';

  const syncResult: MultiStepResults | null =
    syncJobResult ??
    (syncJobError
      ? { success: false, statusCode: 500, error: syncJobError, steps: [] }
      : null);

  const refresh = useCallback(() => {
    setLoading(true);

    fetch(AUTH0_ROLES_ENDPOINT, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
    })
      .then((res) => res.json())
      .then((records: IBAuth0Role[]) => {
        setData(records ?? []);
      })
      .catch(() => {
        notification.error({ message: 'Failed to load Auth0 roles.' });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onEdit = (role: IBAuth0Role) => {
    setEditingRole(role);

    setModalOpen(true);
  };

  const onModalClose = () => {
    setModalOpen(false);

    setEditingRole(null);
  };

  const onSave = (roleId: string, update: RoleUpdateRequest) => {
    fetch(`${AUTH0_ROLES_ENDPOINT}/${roleId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(update),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Save failed');
        notification.success({ message: 'Role mapping updated.' });

        onModalClose();

        refresh();
      })
      .catch(() => {
        notification.error({ message: 'Failed to update role mapping.' });
      });
  };

  const onSync = useCallback(() => {
    if (syncing) {
      return;
    }

    startSync();
  }, [syncing, startSync]);

  useEffect(() => {
    if (syncStatus !== 'completed' && syncStatus !== 'failed') {
      return;
    }

    if (syncStatus === 'failed' && !syncJobResult) {
      notification.error({
        message: 'Failed to sync Auth0 roles.',
        duration: 0,
      });

      return;
    }

    setShowSyncResults(true);

    refresh();
  }, [syncStatus, syncJobResult, refresh]);

  const onCloseSyncResults = useCallback(() => {
    setShowSyncResults(false);
  }, []);

  return (
    <Auth0RolesContext.Provider
      value={{
        loading,
        data,
        editingRole,
        modalOpen,
        syncing,
        syncResult,
        showSyncResults,
        onRefresh: refresh,
        onEdit,
        onModalClose,
        onSave,
        onSync,
        onCloseSyncResults,
      }}
    >
      {children}
    </Auth0RolesContext.Provider>
  );
};

export default Auth0RolesContainer;
